import cx from 'classnames';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { useTranslation } from 'react-i18next';

import { OrganizationFormValues, initialValues, validationSchema } from './config';
import { toCanonicalCountryCode } from './country-code';

import {
  BankRequisitesForm,
  ContactInformationForm,
  KppForm,
  LegalAddressForm,
  OKPOForm,
  PostalAddressForm,
} from 'components/forms/organization';

import { ReactComponent as CloseIcon } from 'assets/images/close.svg';
import { Button } from 'components/ui/Button';
import { useEffect, useMemo, useRef } from 'react';
import { useAlert } from 'react-alert';
import { useSetPaymentOptionMutation } from 'store/api/payments';
import { DefaultPaymentOption, Organization } from 'store/api/types';

import { useUpdateMyOrganizationMutation } from 'store/api/organizations';
import s from './aside-organization.module.scss';

export const AsideOrganization = ({
  isOpen,
  payment,
  handleClose,
  organization,
}: {
  isOpen: boolean;
  handleClose: () => void;
  organization: Organization | undefined;
  payment: DefaultPaymentOption | undefined;
}) => {
  const alert = useAlert();
  const { t } = useTranslation();

  const [updatePaymentOption] = useSetPaymentOptionMutation();
  const [updateMyOrganization] = useUpdateMyOrganizationMutation();

  const isKppRequired = organization?.attributes?.find((item) => item.name === 'kpp');
  const currentCountry = organization?.countryCode;

  const serverValues = useMemo(
    () => initialValues({ organization, payment }),
    [organization, payment],
  );

  // The panel is never unmounted, so Formik has to be reinitialized from the
  // server data — otherwise it keeps the values captured on mount, and the
  // bank fields stay empty whenever the payment options request resolves after
  // the organization one. While the panel is open the snapshot is frozen so a
  // background refetch cannot wipe what the user is typing.
  const formValues = useRef(serverValues);
  if (!isOpen) {
    formValues.current = serverValues;
  }

  // enableReinitialize only reacts to changed values, so a closed panel whose
  // data did not change would keep the edits the user abandoned. Resetting on
  // close makes Cancel discard them without remounting the panel (a remount
  // would break the open/close animation).
  const formikRef = useRef<FormikProps<OrganizationFormValues>>(null);
  useEffect(() => {
    if (!isOpen) {
      formikRef.current?.resetForm({ values: serverValues });
    }
  }, [isOpen, serverValues]);

  const containerClasses = cx(s['container'], {
    [s['open']]: isOpen,
  });
  const wrapperClasses = cx(s['wrapper'], {
    [s['open']]: isOpen,
  });
  const classesButtonBack = cx(s['button'], s['button-back']);
  const classesButtonSave = cx(s['button'], s['button-save']);

  const onSubmit = async (values: OrganizationFormValues) => {
    const { bic, bankId, bankDepartment, paymentAccount, correspondentAccount } = values;
    const { okpo, kpp, email, phone, country, address, postalCountry, postalAddress } = values;

    const bankParams = {
      bic: bic ?? '',
      isDefault: true,
      currency: 'RUB',
      docId: '000000000',
      bankId: bankId ?? '',
      bankName: '',
      name: bankDepartment ?? '',
      accountNumber: paymentAccount ?? '',
      corrAccountNumber: correspondentAccount ?? '',
    };

    // An empty value is only sent for an attribute the organization already
    // has, where it means a deliberate clear. Sending it for an attribute that
    // does not exist would create it: the server merges attributes into the
    // stored document, and an organization without a KPP would then show an
    // empty required "KPP" field forever.
    const storedAttributes = new Set(
      (organization?.attributes ?? []).map((attribute) => attribute.name),
    );

    const organizationAttributes = [
      { name: 'okpo', value: okpo ?? '' },
      { name: 'kpp', value: kpp ?? '' },
      { name: 'email', value: email ?? '' },
      { name: 'phone', value: phone ?? '' },
      { name: 'country', value: country ?? '' },
      { name: 'address', value: address ?? '' },
      { name: 'postal_country', value: postalCountry ?? '' },
      { name: 'postal_address', value: postalAddress ?? '' },
    ].filter(
      (attribute) => attribute.value.trim() !== '' || storedAttributes.has(attribute.name),
    );

    const organizationInput = {
      countryCode: toCanonicalCountryCode(country),
      attributes: organizationAttributes,
    };

    // Exactly one message per submit: the two mutations are independent (there
    // is no combined server mutation yet), so reporting them separately used to
    // show a green "updated" toast next to the red failure of the same Save.
    try {
      await updateMyOrganization({
        input: organizationInput,
      }).unwrap();
    } catch {
      alert.error(t('Organization update failed'));
      return;
    }

    try {
      await updatePaymentOption({
        input: { ...bankParams },
      }).unwrap();
    } catch {
      alert.error(t('Organization data saved, bank requisites were not. Please retry.'));
      return;
    }

    alert.success(t('Organization requisites sucessfully updated'));
    handleClose();
  };

  const scrollToFirstError = (errors: FormikErrors<OrganizationFormValues>) => {
    const allErrorKeys = Object.keys(errors);

    for (const errorKey of allErrorKeys) {
      const errorElement = document.querySelector(`[name="${errorKey}"]`);

      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' });
        break;
      }

      const selectElement = document.getElementById(`${errorKey}`);
      if (selectElement) {
        selectElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });

        break;
      }
    }
  };

  return (
    <Formik
      enableReinitialize
      innerRef={formikRef}
      onSubmit={onSubmit}
      validationSchema={() => validationSchema({ t, organization })}
      initialValues={formValues.current}
    >
      {({ dirty, isSubmitting, submitForm, validateForm }) => {
        return (
          <div className={containerClasses}>
            <div className={wrapperClasses}>
              <div className={s['header']}>
                <span className={s['title']}>{t('Editing')}</span>

                <div
                  role="button"
                  tabIndex={0}
                  className={s['close']}
                  onClick={() => handleClose()}
                  onKeyDown={(e) => {
                    handleClose();
                  }}
                >
                  <CloseIcon />
                </div>
              </div>

              <div className={s['main']}>
                <div className={s['scroll']}>
                  {(currentCountry === 'kg' || currentCountry === 'kgz') && <OKPOForm />}
                  {isKppRequired && <KppForm />}
                  <LegalAddressForm />
                  <PostalAddressForm />
                  <ContactInformationForm country={currentCountry ?? ''} />
                  <BankRequisitesForm />
                </div>
              </div>

              <div className={s['footer']}>
                <Button
                  displayType="outlined-secondary"
                  className={classesButtonBack}
                  onClick={() => handleClose()}
                >
                  {t('Cancel')}
                </Button>
                <Button
                  onClick={() => {
                    // Scrolling by the errors captured in this render would rely
                    // on validateOnChange having already refreshed them; the
                    // validation result is authoritative.
                    validateForm()
                      .then((validationErrors) => {
                        if (Object.keys(validationErrors).length > 0) {
                          scrollToFirstError(validationErrors);
                          return undefined;
                        }

                        return submitForm();
                      })
                      .catch(() => undefined);
                  }}
                  className={classesButtonSave}
                  disabled={isSubmitting || !dirty}
                >
                  {t('Save')}
                </Button>
              </div>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
